import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import Rating, { type RatingProps } from '@mui/material/Rating';
import { useState, forwardRef } from 'react';

import { StarRoundedIcon } from '../../icons';
import { Text } from '../../text';

type RatingInputProps = RatingProps & {
  label?: string;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
  required?: boolean;
  onBlur?: () => void;
};

const labels: { [index: string]: string } = {
  1: 'แย่มาก',
  2: 'แย่',
  3: 'ปานกลาง',
  4: 'ดี',
  5: 'ดีมาก',
};

const RatingInput = forwardRef<HTMLSpanElement, RatingInputProps>(
  (props: RatingInputProps, ref) => {
    const {
      label,
      helperText,
      defaultValue,
      error: hasError,
      value,
      required: isRequired,
      onChange,
      onBlur,
      sx,
      ...restProps
    } = props;

    const [ratingValue, setRatingValue] = useState(value ?? defaultValue ?? 0);

    const handleChange = (event: React.SyntheticEvent<Element, Event>, value: number | null) => {
      setRatingValue(value ?? 0);
      onChange?.(event, value);
      onBlur?.();
    };

    let description = null;
    if (ratingValue) {
      description = (
        <Text mt={1} textAlign='center' fontWeight={500}>
          {labels[ratingValue]}
        </Text>
      );
    }

    return (
      <FormControl error={hasError} required={isRequired} sx={sx}>
        {label ? <InputLabel sx={{ position: 'relative', mb: 1 }}>{label}</InputLabel> : null}
        <Rating
          value={ratingValue}
          icon={<StarRoundedIcon sx={{ color: '#ffb400', fontSize: 65 }} />}
          emptyIcon={<StarRoundedIcon sx={{ color: 'grey.200', fontSize: 65 }} />}
          {...restProps}
          onChange={handleChange}
          ref={ref}
        />
        {description}
        {helperText ? <FormHelperText sx={{ mt: 1 }}>{helperText}</FormHelperText> : null}
      </FormControl>
    );
  },
);

RatingInput.displayName = 'RatingInput';

export type { RatingInputProps };
export { RatingInput };
